// src/utils/qrCode.ts
/**
 * QR code generation for the phone handoff (Issue #1071).
 *
 * The handoff URL has to appear as a scannable code in the identification
 * dialog. We generate the matrix ourselves instead of pulling in a QR library:
 * the module is small, has no runtime dependencies and the caller only needs a
 * boolean matrix it can render as SVG rectangles.
 *
 * Scope on purpose: byte mode (UTF-8), error correction level M, versions 1–10
 * (up to 213 bytes) — far more than a handoff link needs. Everything else of
 * ISO/IEC 18004 is implemented in full, including the mask evaluation, so
 * ordinary phone cameras read the result.
 */

/** A generated code: `matrix[row][col]` is `true` for a dark module. */
export type QrMatrix = boolean[][]

/** Longest input we encode — version 10 at error correction level M. */
export const QR_MAX_BYTES = 213

/**
 * Block layout per version at error correction level M.
 *
 * `data` = data codewords in total, `ec` = error correction codewords per
 * block, `blocks` = number of blocks. The blocks split evenly; the longer ones
 * carry exactly one codeword more (ISO/IEC 18004 table 9).
 */
const VERSIONS: readonly { data: number; ec: number; blocks: number }[] = [
  { data: 16, ec: 10, blocks: 1 }, // version 1
  { data: 28, ec: 16, blocks: 1 },
  { data: 44, ec: 26, blocks: 1 },
  { data: 64, ec: 18, blocks: 2 },
  { data: 86, ec: 24, blocks: 2 },
  { data: 108, ec: 16, blocks: 4 },
  { data: 124, ec: 18, blocks: 4 },
  { data: 154, ec: 22, blocks: 4 },
  { data: 182, ec: 22, blocks: 5 },
  { data: 216, ec: 26, blocks: 5 }, // version 10
]

/** Row/column centres of the alignment patterns per version (index = v - 1). */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
]

// ---------------------------------------------------------------------------
// Arithmetic in GF(256) — the field the Reed-Solomon codewords live in
// ---------------------------------------------------------------------------

const EXP = new Uint8Array(512)
const LOG = new Uint8Array(256)

for (let i = 0, x = 1; i < 255; i++) {
  EXP[i] = x
  LOG[x] = i
  x <<= 1
  if (x & 0x100) x ^= 0x11d
}
for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]

function mul(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]
}

/** Generator polynomial of degree `count`, highest power first. */
function generatorPoly(count: number): number[] {
  let poly = [1]
  for (let i = 0; i < count; i++) {
    const next = new Array<number>(poly.length + 1).fill(0)
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j]
      next[j + 1] ^= mul(poly[j], EXP[i])
    }
    poly = next
  }
  return poly
}

/** The `count` error correction codewords of one block. */
function errorCorrection(data: readonly number[], count: number): number[] {
  const generator = generatorPoly(count)
  const remainder = new Array<number>(data.length + count).fill(0)
  data.forEach((byte, index) => {
    remainder[index] = byte
  })
  for (let i = 0; i < data.length; i++) {
    const factor = remainder[i]
    if (factor === 0) continue
    for (let j = 0; j < generator.length; j++) {
      remainder[i + j] ^= mul(generator[j], factor)
    }
  }
  return remainder.slice(data.length)
}

// ---------------------------------------------------------------------------
// Bit stream: mode, length, payload, padding
// ---------------------------------------------------------------------------

/** Bits of the character count field — 16 from version 10 on (byte mode). */
function countBits(version: number): number {
  return version < 10 ? 8 : 16
}

/** Smallest version that holds `byteCount` bytes, or `null` if none does. */
function pickVersion(byteCount: number): number | null {
  for (let version = 1; version <= VERSIONS.length; version++) {
    const capacity = VERSIONS[version - 1].data * 8 - 4 - countBits(version)
    if (byteCount * 8 <= capacity) return version
  }
  return null
}

/** Data codewords of the whole message, already padded to the full capacity. */
function dataCodewords(bytes: Uint8Array, version: number): number[] {
  const total = VERSIONS[version - 1].data
  const bits: number[] = []
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1)
  }

  push(0b0100, 4) // byte mode
  push(bytes.length, countBits(version))
  bytes.forEach((byte) => push(byte, 8))

  // Terminator, then fill up to a whole codeword
  const capacity = total * 8
  push(0, Math.min(4, capacity - bits.length))
  while (bits.length % 8 !== 0) bits.push(0)

  const codewords: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]
    codewords.push(byte)
  }
  // The standard pad bytes, alternating
  for (let i = 0; codewords.length < total; i++) codewords.push(i % 2 === 0 ? 0xec : 0x11)
  return codewords
}

/** Data and error correction codewords, interleaved block by block. */
function interleave(codewords: readonly number[], version: number): number[] {
  const { data: total, ec: ecCount, blocks: blockCount } = VERSIONS[version - 1]
  const shortLength = Math.floor(total / blockCount)
  const longBlocks = total % blockCount

  const dataBlocks: number[][] = []
  const ecBlocks: number[][] = []
  let offset = 0
  for (let block = 0; block < blockCount; block++) {
    const length = shortLength + (block >= blockCount - longBlocks ? 1 : 0)
    const chunk = codewords.slice(offset, offset + length)
    offset += length
    dataBlocks.push(chunk)
    ecBlocks.push(errorCorrection(chunk, ecCount))
  }

  const result: number[] = []
  for (let i = 0; i < shortLength + 1; i++) {
    dataBlocks.forEach((block) => {
      if (i < block.length) result.push(block[i])
    })
  }
  for (let i = 0; i < ecCount; i++) {
    ecBlocks.forEach((block) => result.push(block[i]))
  }
  return result
}

// ---------------------------------------------------------------------------
// The matrix: function patterns, payload, mask
// ---------------------------------------------------------------------------

interface Canvas {
  size: number
  modules: boolean[][]
  /** Function patterns and reserved areas — never touched by data or mask. */
  fixed: boolean[][]
}

function createCanvas(size: number): Canvas {
  return {
    size,
    modules: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
    fixed: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  }
}

function setFixed(canvas: Canvas, row: number, col: number, dark: boolean): void {
  if (row < 0 || col < 0 || row >= canvas.size || col >= canvas.size) return
  canvas.modules[row][col] = dark
  canvas.fixed[row][col] = true
}

/** Finder pattern plus its separator, centred on (`row`, `col`). */
function drawFinder(canvas: Canvas, row: number, col: number): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy))
      setFixed(canvas, row + dy, col + dx, distance !== 2 && distance <= 3)
    }
  }
}

function drawFunctionPatterns(canvas: Canvas, version: number): void {
  const last = canvas.size - 1

  // Timing patterns first — the finders overwrite their own corners
  for (let i = 0; i < canvas.size; i++) {
    setFixed(canvas, 6, i, i % 2 === 0)
    setFixed(canvas, i, 6, i % 2 === 0)
  }

  drawFinder(canvas, 3, 3)
  drawFinder(canvas, 3, last - 3)
  drawFinder(canvas, last - 3, 3)

  // Alignment patterns, except where they would sit on a finder
  const centers = ALIGNMENT_CENTERS[version - 1]
  centers.forEach((row) => {
    centers.forEach((col) => {
      const onFinder =
        (row === 6 && col === 6) ||
        (row === 6 && col === last - 6) ||
        (row === last - 6 && col === 6)
      if (onFinder) return
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          setFixed(canvas, row + dy, col + dx, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
        }
      }
    })
  })

  // Reserve the format information areas (written per mask later). Index 6 is
  // left out on purpose — there the timing patterns cross, and they stay.
  for (let i = 0; i <= 8; i++) {
    if (i === 6) continue
    setFixed(canvas, 8, i, false)
    setFixed(canvas, i, 8, false)
  }
  for (let i = 0; i < 8; i++) {
    setFixed(canvas, 8, last - i, false)
    setFixed(canvas, last - i, 8, false)
  }
  setFixed(canvas, last - 7, 8, true) // the always dark module

  if (version >= 7) drawVersionInfo(canvas, version)
}

/** 18 bit version information, mirrored next to the lower and right finder. */
function drawVersionInfo(canvas: Canvas, version: number): void {
  let remainder = version
  for (let i = 0; i < 12; i++) {
    remainder = (remainder << 1) ^ ((remainder >> 11) * 0x1f25)
  }
  const bits = (version << 12) | remainder

  for (let i = 0; i < 18; i++) {
    const dark = ((bits >> i) & 1) === 1
    const a = canvas.size - 11 + (i % 3)
    const b = Math.floor(i / 3)
    setFixed(canvas, b, a, dark)
    setFixed(canvas, a, b, dark)
  }
}

/** 15 bit format information (level M + mask), in both of its copies. */
function drawFormatInfo(canvas: Canvas, mask: number): void {
  const data = (0b00 << 3) | mask // 0b00 = error correction level M
  let remainder = data << 10
  for (let i = 4; i >= 0; i--) {
    if ((remainder >> (i + 10)) & 1) remainder ^= 0x537 << i
  }
  const bits = ((data << 10) | remainder) ^ 0x5412
  const bit = (index: number): boolean => ((bits >> index) & 1) === 1
  const last = canvas.size - 1

  for (let i = 0; i <= 5; i++) setFixed(canvas, i, 8, bit(i))
  setFixed(canvas, 7, 8, bit(6))
  setFixed(canvas, 8, 8, bit(7))
  setFixed(canvas, 8, 7, bit(8))
  for (let i = 9; i < 15; i++) setFixed(canvas, 8, 14 - i, bit(i))

  for (let i = 0; i < 8; i++) setFixed(canvas, 8, last - i, bit(i))
  for (let i = 8; i < 15; i++) setFixed(canvas, last - 14 + i, 8, bit(i))
  setFixed(canvas, last - 7, 8, true)
}

/** The payload, snaking upwards and downwards through the free modules. */
function drawCodewords(canvas: Canvas, codewords: readonly number[]): void {
  let bitIndex = 0
  const bitCount = codewords.length * 8

  for (let right = canvas.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5 // the vertical timing pattern is skipped
    for (let vertical = 0; vertical < canvas.size; vertical++) {
      for (let column = 0; column < 2; column++) {
        const col = right - column
        const upward = ((right + 1) & 2) === 0
        const row = upward ? canvas.size - 1 - vertical : vertical
        if (canvas.fixed[row][col] || bitIndex >= bitCount) continue
        const byte = codewords[bitIndex >> 3]
        canvas.modules[row][col] = ((byte >> (7 - (bitIndex & 7))) & 1) === 1
        bitIndex++
      }
    }
  }
}

/** The eight mask conditions of the standard, by mask number. */
function maskApplies(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0
    case 1:
      return row % 2 === 0
    case 2:
      return col % 3 === 0
    case 3:
      return (row + col) % 3 === 0
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0
    default:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0
  }
}

/** Runs of five and more, 2×2 blocks, finder look-alikes, dark/light ratio. */
function penalty(modules: readonly boolean[][]): number {
  const size = modules.length
  let score = 0

  const finderLike = (line: readonly boolean[], at: number): boolean => {
    const pattern = [true, false, true, true, true, false, true]
    for (let i = 0; i < 7; i++) if (line[at + i] !== pattern[i]) return false
    const before = line.slice(Math.max(0, at - 4), at)
    const after = line.slice(at + 7, at + 11)
    const quiet = (part: readonly boolean[]) => part.length === 4 && part.every((m) => !m)
    return quiet(before) || quiet(after)
  }

  const scoreLine = (line: readonly boolean[]): void => {
    let run = 1
    for (let i = 1; i < size; i++) {
      if (line[i] === line[i - 1]) run++
      else {
        if (run >= 5) score += 3 + (run - 5)
        run = 1
      }
    }
    if (run >= 5) score += 3 + (run - 5)
    for (let i = 0; i + 7 <= size; i++) if (finderLike(line, i)) score += 40
  }

  for (let row = 0; row < size; row++) scoreLine(modules[row])
  for (let col = 0; col < size; col++) scoreLine(modules.map((line) => line[col]))

  for (let row = 0; row < size - 1; row++) {
    for (let col = 0; col < size - 1; col++) {
      const value = modules[row][col]
      if (
        modules[row][col + 1] === value &&
        modules[row + 1][col] === value &&
        modules[row + 1][col + 1] === value
      ) {
        score += 3
      }
    }
  }

  const dark = modules.reduce((sum, line) => sum + line.filter(Boolean).length, 0)
  const percent = (dark * 100) / (size * size)
  score += Math.floor(Math.abs(percent - 50) / 5) * 10

  return score
}

/**
 * The QR matrix of `text` — `matrix[row][col] === true` means a dark module.
 *
 * Returns `null` for an empty text or one longer than {@link QR_MAX_BYTES}:
 * the caller shows the link instead of a code it could not draw.
 */
export function qrMatrix(text: string): QrMatrix | null {
  if (!text) return null
  const bytes = new TextEncoder().encode(text)
  const version = bytes.length > QR_MAX_BYTES ? null : pickVersion(bytes.length)
  if (version === null) return null

  const codewords = interleave(dataCodewords(bytes, version), version)
  const base = createCanvas(17 + 4 * version)
  drawFunctionPatterns(base, version)
  drawCodewords(base, codewords)

  let best: boolean[][] | null = null
  let bestScore = Number.POSITIVE_INFINITY
  for (let mask = 0; mask < 8; mask++) {
    const candidate: Canvas = {
      size: base.size,
      modules: base.modules.map((line) => [...line]),
      fixed: base.fixed,
    }
    for (let row = 0; row < candidate.size; row++) {
      for (let col = 0; col < candidate.size; col++) {
        if (!base.fixed[row][col] && maskApplies(mask, row, col)) {
          candidate.modules[row][col] = !candidate.modules[row][col]
        }
      }
    }
    drawFormatInfo(candidate, mask)
    const score = penalty(candidate.modules)
    if (score < bestScore) {
      bestScore = score
      best = candidate.modules
    }
  }

  return best
}
